import apiServer from "./apiServer";
import {
  setUser,
  logout as logOutApp,
  setSessionTime
} from "../slices/authReducer";
import {
  API_KEY,
  MOMENT_KEY,
  SESSION_TIME,
  STORAGE_KEY,
  STORAGE_KEY_REFRESH
} from "../utils/config";
import { remoteConfigGetter } from "../utils/remoteConfig";
import axios, { type AxiosRequestHeaders, type AxiosResponse } from "axios";
import moment from 'moment';
import { type NavigateFunction } from "react-router-dom";
import { type Dispatch } from "@reduxjs/toolkit";
import {
  type LoginInfo,
  type RefreshData,
  normalizeUserData,
  isValidLoginInfo,
  validateUserData
} from "../models/login";

// ==================== INTERFACES ====================

/**
 * Option chain data request payload
 */
interface OptionChainDataPayload {
  symbol?: string;
  strike?: number;
  expiry?: string;
  [key: string]: any;
}

/**
 * Logout terminal request payload  
 */
interface LogoutTerminalPayload {
  [key: string]: any;
}

/**
 * Basic config data structure
 */
interface BasicConfigData {
  api_key: string;
  source: string;
  [key: string]: any;
}

/**
 * User session flow parameters
 */
interface UserSessionFlowParams {
  user: LoginInfo;
  refreshData: RefreshData | any;
  dispatch: Dispatch;
  isSetLocal?: boolean;
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Create standard headers for API requests
 */
function createStandardHeaders(token: string, includeStageHeaders: boolean = true): AxiosRequestHeaders {
  const headers: AxiosRequestHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  if (includeStageHeaders) {
    headers['X-Application-Id'] = String(process.env.REACT_APP_TV_APPLICATION_ID);
    headers['X-Stage'] = String(process.env.REACT_APP_ENV);
  }

  return headers;
}

/**
 * Get user data from localStorage safely
 */
function getUserDataFromStorage(): { userData: LoginInfo | null; token: string | null } {
  try {
    if (typeof window === 'undefined') {
      return { userData: null, token: null };
    }

    const storedData = localStorage.getItem(STORAGE_KEY);
    const userData = storedData ? JSON.parse(storedData) : null;
    const token = userData?.access_token || null;

    return { userData, token };
  } catch (error) {
    console.error('Error reading user data from localStorage:', error);
    return { userData: null, token: null };
  }
}

/**
 * Clear all authentication storage
 */
function clearAuthStorage(): void {
  if (typeof window === 'undefined') return;

  const keysToRemove = [
    STORAGE_KEY,
    MOMENT_KEY,
    STORAGE_KEY_REFRESH,
    'dock_universal_token',
    'user_id',
    'user_client_code'
  ];

  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove ${key} from localStorage:`, error);
    }
  });
}

/**
 * Set session time and persist it
 */
function setTimeSession(dispatch: Dispatch): void {
  try {
    const momentInTime = moment().add(SESSION_TIME, 'minutes');
    const sessionTime = momentInTime.valueOf();

    dispatch(setSessionTime(sessionTime));

    if (typeof window !== 'undefined') {
      localStorage.setItem(MOMENT_KEY, sessionTime.toString());
    }
  } catch (error) {
    console.error('Error setting session time:', error);
  }
}

// ==================== API FUNCTIONS ====================

/**
 * Logout from terminal/server
 */
async function logoutTerminal(
  formValues: LogoutTerminalPayload,
  token: string
): Promise<AxiosResponse> {
  try {
    const headers = createStandardHeaders(token, false);
    const url = remoteConfigGetter('vortex_url_v2').asString() + 'user/session';

    return await axios.delete(url, { headers });
  } catch (error) {
    console.error('Logout terminal API error:', error);
    throw error;
  }
}

/**
 * Get option chain data
 */
async function optionChaindata(
  formValues: OptionChainDataPayload,
  token: string
): Promise<AxiosResponse> {
  try {
    const headers = createStandardHeaders(token);
    const url = remoteConfigGetter('vortex_url_v2').asString() + 'aux/scrip/optiondata';

    return await axios.post(url, formValues, { headers });
  } catch (error) {
    console.error('Option chain data API error:', error);
    throw error;
  }
}

/**
 * Get new option chain data (strategies)
 */
async function newOptionChaindata(
  formValues: OptionChainDataPayload,
  token: string
): Promise<AxiosResponse> {
  try {
    const headers = createStandardHeaders(token);
    const url = remoteConfigGetter('vortex_url_v2').asString() + 'strategies/option_chain';

    return await axios.post(url, formValues, { headers });
  } catch (error) {
    console.error('New option chain data API error:', error);
    throw error;
  }
}

// ==================== MAIN FUNCTIONS ====================

/**
 * Enhanced logout function with proper error handling and TypeScript support
 */
const logout = (navigate?: NavigateFunction, redirectPath?: string) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      const { userData, token } = getUserDataFromStorage();

      if (token) {
        try {
          await logoutTerminal({}, token);
        } catch (error) {
          console.error("Error in logout API:", error);
          // Continue with local logout even if server logout fails
        }
      }
    } catch (error) {
      console.error("Error during logout process:", error);
    } finally {
      // Always clear local storage and update state
      clearAuthStorage();
      dispatch(logOutApp());

      if (navigate && redirectPath) {
        navigate(redirectPath, { replace: true });
      }
    }
  };

/**
 * Local logout only (clear storage without API call)
 */
const logoutLocal = (): void => {
  clearAuthStorage();
};

/**
 * Get basic config data with API key and source
 */
function getConfigDataBasic(formValues: any): BasicConfigData {
  return {
    ...formValues,
    api_key: API_KEY,
  };
}

/**
 * Save user session with full validation and error handling
 */
function saveUserSessionFlow({
  user,
  refreshData,
  dispatch,
  isSetLocal = true
}: UserSessionFlowParams): void {
  try {
    // Validate user data before saving
    const validation = validateUserData(user);
    if (!validation.isValid) {
      console.error('Invalid user data for session:', validation.errors);
      throw new Error('Invalid user data: ' + validation.errors.join(', '));
    }

    // Normalize user data to ensure consistency
    const normalizedUser = normalizeUserData(user);

    // Update Redux state
    dispatch(setUser(normalizedUser));

    // Set session time
    setTimeSession(dispatch);

    // Persist to localStorage if requested
    if (isSetLocal && typeof window !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedUser));
        localStorage.setItem(STORAGE_KEY_REFRESH, JSON.stringify(refreshData));

        // Also store individual items for backward compatibility
        if (normalizedUser.access_token) {
          localStorage.setItem('dock_universal_token', normalizedUser.access_token);
        }
        if (normalizedUser.user_id) {
          localStorage.setItem('user_id', normalizedUser.user_id);
        }

      } catch (storageError) {
        console.error('Error saving to localStorage:', storageError);
        // Don't throw here as the session is still valid in Redux
      }
    }
  } catch (error) {
    console.error('Error in saveUserSessionFlow:', error);
    throw error;
  }
}

/**
 * Restore user session from localStorage
 */
function restoreUserSession(dispatch: Dispatch): boolean {
  try {
    const { userData, token } = getUserDataFromStorage();

    if (userData && token && isValidLoginInfo(userData)) {
      const validation = validateUserData(userData);

      if (validation.isValid) {
        // Restore session
        dispatch(setUser(userData));

        // Check and restore session time
        const storedSessionTime = localStorage.getItem(MOMENT_KEY);
        if (storedSessionTime) {
          const sessionTime = parseInt(storedSessionTime, 10);
          const currentTime = moment().valueOf();

          if (sessionTime > currentTime) {
            dispatch(setSessionTime(sessionTime));
            return true;
          } else {
            clearAuthStorage();
            return false;
          }
        } else {
          // No session time found, set a new one
          setTimeSession(dispatch);
          return true;
        }
      } else {
        console.warn('Stored user data validation failed:', validation.errors);
        clearAuthStorage();
        return false;
      }
    }

    return false;
  } catch (error) {
    console.error('Error restoring user session:', error);
    clearAuthStorage();
    return false;
  }
}

/**
 * Check if current session is valid
 */
function isSessionValid(): boolean {
  try {
    if (typeof window === 'undefined') return false;

    const { userData, token } = getUserDataFromStorage();
    const storedSessionTime = localStorage.getItem(MOMENT_KEY);

    if (!userData || !token || !storedSessionTime) {
      return false;
    }

    const sessionTime = parseInt(storedSessionTime, 10);
    const currentTime = moment().valueOf();

    return sessionTime > currentTime;
  } catch (error) {
    console.error('Error checking session validity:', error);
    return false;
  }
}

/**
 * Refresh session time
 */
function refreshSession(dispatch: Dispatch): void {
  if (isSessionValid()) {
    setTimeSession(dispatch);
  } else {
    console.log('Session invalid, cannot refresh');
  }
}

// ==================== AUTH SERVICE OBJECT ====================

const authService = {
  // Main functions
  logout,
  logoutLocal,
  saveUserSessionFlow,
  restoreUserSession,

  // API functions
  optionChaindata,
  newOptionChaindata,

  // Utility functions
  getConfigDataBasic,
  isSessionValid,
  refreshSession,
  setTimeSession,

  // Storage functions
  getUserDataFromStorage,
  clearAuthStorage,

  // Validation functions
  validateUserSession: (user: LoginInfo) => validateUserData(user),
  normalizeUserData: (user: any) => normalizeUserData(user),
};

// ==================== TYPE EXPORTS ====================

export type {
  OptionChainDataPayload,
  LogoutTerminalPayload,
  BasicConfigData,
  UserSessionFlowParams
};

export default authService;